import {
  APIProvider,
  Map as GoogleMap,
  useMap,
} from "@vis.gl/react-google-maps";
import { memo, useEffect, useState } from "react";

import { DirectionsPolyline, Marker } from "@web-app/components";
import config from "@web-app/config";

import { options } from "../../DispatchMap/utils";

import { MapProps } from "./types";

const MapContent = memo(
  ({
    pickupLocation,
    dropoffLocation,
    onMapLoad,
    directionsService,
    directionsRenderer,
    mapRef,
  }: MapProps & { mapRef: { current: google.maps.Map | null } }) => {
    const map = useMap();

    useEffect(() => {
      if (map && !mapRef.current) {
        mapRef.current = map;
        onMapLoad(map);
        if (directionsRenderer) {
          directionsRenderer.setMap(map);
        }
      }
    }, [map, mapRef, onMapLoad, directionsRenderer]);

    useEffect(() => {
      if (!map) return;

      const hasPickup = pickupLocation?.geometry?.location;
      const hasDropoff = dropoffLocation?.geometry?.location;

      if (hasPickup && hasDropoff) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(
          new google.maps.LatLng(
            pickupLocation.geometry!.location!.lat,
            pickupLocation.geometry!.location!.lng,
          ),
        );
        bounds.extend(
          new google.maps.LatLng(
            dropoffLocation.geometry!.location!.lat,
            dropoffLocation.geometry!.location!.lng,
          ),
        );
        map.fitBounds(bounds);
      } else if (hasPickup) {
        map.setCenter({
          lat: pickupLocation.geometry!.location!.lat,
          lng: pickupLocation.geometry!.location!.lng,
        });
        map.setZoom(16);
      } else if (hasDropoff) {
        map.setCenter({
          lat: dropoffLocation.geometry!.location!.lat,
          lng: dropoffLocation.geometry!.location!.lng,
        });
        map.setZoom(16);
      }
    }, [map, pickupLocation, dropoffLocation]);

    return (
      <>
        {/* Directions polyline between pickup and dropoff */}
        <DirectionsPolyline
          pickupLocation={pickupLocation}
          dropoffLocation={dropoffLocation}
          directionsService={directionsService}
        />

        {pickupLocation?.geometry?.location && (
          <Marker
            type="pickup"
            position={{
              lat: pickupLocation.geometry.location.lat,
              lng: pickupLocation.geometry.location.lng,
            }}
            icon="/markers/PICKUP_TODO.webp"
            alt="Pickup location marker"
            scale={1.2}
            routePositionColor="#000000"
          />
        )}
        {dropoffLocation?.geometry?.location && (
          <Marker
            type="dropoff"
            position={{
              lat: dropoffLocation.geometry.location.lat,
              lng: dropoffLocation.geometry.location.lng,
            }}
            icon="/markers/DROPOFF_TODO.webp"
            alt="Dropoff location marker"
            scale={1.2}
            routePositionColor="#000000"
          />
        )}
      </>
    );
  },
);

export default function Map({
  pickupLocation,
  dropoffLocation,
  onMapLoad,
  directionsService,
  directionsRenderer,
  map,
}: MapProps) {
  const isLoaded = true;
  const mapRef = { current: map };

  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({
    lat: 40.7128,
    lng: -74.006,
  });

  useEffect(() => {
    if (pickupLocation?.geometry?.location) {
      setMapCenter({
        lat: pickupLocation.geometry.location.lat,
        lng: pickupLocation.geometry.location.lng,
      });
    } else if (dropoffLocation?.geometry?.location) {
      setMapCenter({
        lat: dropoffLocation.geometry.location.lat,
        lng: dropoffLocation.geometry.location.lng,
      });
    }
  }, [pickupLocation, dropoffLocation]);

  if (!isLoaded) return null;

  return (
    <div className="dispatch-createOrder-map relative h-full w-full bg-gray-200">
      <APIProvider apiKey={config.GOOGLE_MAPS_API_KEY || ""}>
        <GoogleMap
          className="rounded-xl border-2 border-white transition ease-in focus:outline-none focus:ring-none w-full h-full"
          style={{
            bottom: 0,
            left: 0,
            position: "absolute",
            right: 0,
            top: 0,
          }}
          defaultCenter={mapCenter}
          defaultZoom={18}
          mapId={config.GOOGLE_MAPS_ID}
          fullscreenControl={false}
          mapTypeControl={false}
          streetViewControl={false}
          gestureHandling={options.gestureHandling}
          disableDefaultUI={options.disableDefaultUI}
          clickableIcons={options.clickableIcons}
        >
          <MapContent
            pickupLocation={pickupLocation}
            dropoffLocation={dropoffLocation}
            onMapLoad={onMapLoad}
            directionsService={directionsService}
            directionsRenderer={directionsRenderer}
            map={map}
            mapRef={mapRef}
          />
        </GoogleMap>
      </APIProvider>
    </div>
  );
}
